// src/modules/admin/pages/AdminCompetitions/index.js
import React, { useState, useCallback, useEffect } from 'react';
import { Container, Badge, Alert, Button, Dropdown, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { useService } from '../../../../shared/hooks/useService';
import { useAdminData } from '../../hooks/useAdminData';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import { formatDate } from '../../../../shared/utils/dateUtils';
import Loading from '../../../../shared/components/Loading';
import ErrorState from '../../../../shared/components/ErrorState';
import './styles.css';

const TAB_OPTIONS = ['all', 'active', 'upcoming', 'concluded'];

const TAB_LABELS = {
    all: 'All',
    active: 'Active',
    upcoming: 'Upcoming',
    concluded: 'Concluded',
};

const ADMIN_FORCE_STATE_OPTIONS = ['upcoming', 'active', 'concluded'];

const AdminCompetitions = () => {
    const service = useService();
    const { hasPermission } = useAuth();
    const hasEditPermission = hasPermission('competition:edit');
    const hasCreatePermission = hasPermission('competition:create');
    const hasCompetitionPermission = hasEditPermission || hasCreatePermission;
    const [activeTab, setActiveTab] = useState('all');
    const [competitions, setCompetitions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncResult, setSyncResult] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [forcingState, setForcingState] = useState(null);
    const { isWarmup } = useWarmupTimer(isLoading);
    const { stats, refetch: refetchStats } = useAdminData();

    const fetchCompetitions = useCallback(async (state) => {
        try {
            const data = await service.getAllCompetitions(state);
            setCompetitions(data || []);
        } catch (err) {
            setError(err.message || 'Failed to fetch competitions');
        } finally {
            setIsLoading(false);
        }
    }, [service]);

    useEffect(() => {
        const state = activeTab === 'all' ? null : activeTab;
        setIsLoading(true);
        setError(null);
        setSyncResult(null);
        fetchCompetitions(state);
    }, [activeTab, fetchCompetitions]);

    const handleSyncStates = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const result = await service.syncCompetitionStates();
            setSyncResult({ type: 'success', data: result.summary });
            const state = activeTab === 'all' ? null : activeTab;
            fetchCompetitions(state);
            refetchStats();
        } catch (err) {
            setSyncResult({ type: 'error', message: err.message || 'Failed to sync states' });
        } finally {
            setSyncing(false);
        }
    };

    const handleForceState = async (competitionId, newState) => {
        if (!window.confirm(`Change competition state to "${newState}"? This cannot be easily undone.`)) {
            return;
        }
        setForcingState(competitionId);
        try {
            await service.forceCompetitionState(competitionId, newState);
            const state = activeTab === 'all' ? null : activeTab;
            fetchCompetitions(state);
            refetchStats();
        } catch (err) {
            setError(err.message || 'Failed to update competition state');
        } finally {
            setForcingState(null);
        }
    };

    const getStatusBadgeVariant = (state) => {
        if (state === 'active') return 'success';
        if (state === 'upcoming') return 'primary';
        if (state === 'concluded') return 'secondary';
        return 'secondary';
    };

    const getStatusLabel = (state) => {
        if (state === 'active') return 'Active';
        if (state === 'upcoming') return 'Upcoming';
        if (state === 'concluded') return 'Concluded';
        return state;
    };

    const getParticipantCount = (competition) =>
        competition.participantCount || competition.competitors?.length || 0;

    const getWinnerName = (competition) =>
        competition.winnerName || competition.winner || null;

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (!hasCompetitionPermission) {
        return (
            <Container className="admin-competitions-container">
                <Alert variant="warning" className="admin-no-permission-alert">
                    <Alert.Heading>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No Permission
                    </Alert.Heading>
                    <p>You need the <strong>competition:edit</strong> or <strong>competition:create</strong> permission to access competitions.</p>
                    <Button variant="outline-warning" size="sm" as={Link} to="/admin">
                        <i className="bi bi-arrow-left me-1"></i>
                        Back to Dashboard
                    </Button>
                </Alert>
            </Container>
        );
    }
    if (error) return <ErrorState message={error} onRetry={() => { fetchCompetitions(activeTab === 'all' ? null : activeTab); }} />;

    const counts = {
        active: stats?.activeCompetitions ?? 0,
        upcoming: stats?.upcomingCompetitions ?? 0,
        concluded: stats?.concludedCompetitions ?? 0,
    };

    return (
        <Container className="admin-competitions-container">
            {/* Sync States section — edit-only */}
            {hasEditPermission && (
                <div className="admin-competitions-sync-section mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">Competition State Management</h4>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleSyncStates}
                            disabled={syncing}
                        >
                            {syncing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Syncing…
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-arrow-clockwise me-1"></i>
                                    Sync States
                                </>
                            )}
                        </Button>
                    </div>

                    {syncResult && (
                        <Alert
                            variant={syncResult.type === 'success' ? 'success' : 'danger'}
                            dismissible
                            className="admin-competitions-sync-alert"
                        >
                            <Alert.Heading>
                                {syncResult.type === 'success' ? 'Sync Complete' : 'Sync Failed'}
                            </Alert.Heading>
                            {syncResult.type === 'success' && syncResult.data ? (
                                <div className="admin-competitions-sync-summary">
                                    {Object.entries(syncResult.data).map(([key, value]) => (
                                        <div key={key} className="admin-competitions-sync-stat">
                                            <strong>{TAB_LABELS[key] || key}:</strong>{' '}
                                            <Badge bg={getStatusBadgeVariant(key)}>{value}</Badge>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>{syncResult.message}</p>
                            )}
                        </Alert>
                    )}
                </div>
            )}

            {/* Page header with create button */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Competitions</h4>
                {hasCreatePermission && (
                    <Button variant="primary" size="sm" as={Link} to="/competitions/create">
                        <i className="bi bi-plus-lg me-1"></i>
                        Create Competition
                    </Button>
                )}
            </div>

            {/* State filter tabs */}
            <div className="admin-competitions-tabs">
                {TAB_OPTIONS.map(tab => {
                    const isActive = activeTab === tab;
                    const count = tab === 'all'
                        ? counts.active + counts.upcoming + counts.concluded
                        : counts[tab] ?? 0;
                    return (
                        <button
                            key={tab}
                            className={`admin-competitions-tab ${isActive ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab)}
                            disabled={isActive}
                        >
                            {TAB_LABELS[tab]}
                            <Badge bg={isActive ? 'primary' : 'secondary'} className="admin-competitions-count">
                                {count}
                            </Badge>
                        </button>
                    );
                })}
            </div>

            {/* Competitions table */}
            {competitions.length > 0 ? (
                <div className="table-responsive">
                    <Table hover className="admin-competitions-table mb-0">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Start</th>
                                <th>End</th>
                                <th className="text-center">Competitors</th>
                                <th>Winner</th>
                                {hasEditPermission && <th className="text-end">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {competitions.map(competition => (
                                <tr key={competition.competitionId} className={`admin-competition-row-${competition.state}`}>
                                    <td className="admin-competition-name-cell">
                                        {competition.name}
                                    </td>
                                    <td>
                                        <Badge bg={getStatusBadgeVariant(competition.state)}>
                                            {getStatusLabel(competition.state)}
                                        </Badge>
                                    </td>
                                    <td className="text-nowrap">
                                        {formatDate(competition.startDate, 'MMM d, yyyy')}
                                    </td>
                                    <td className="text-nowrap">
                                        {formatDate(competition.endDate, 'MMM d, yyyy')}
                                    </td>
                                    <td className="text-center">
                                        {getParticipantCount(competition)}
                                    </td>
                                    <td>
                                        {getWinnerName(competition) ? (
                                            <span>
                                                <i className="bi bi-trophy-fill text-warning me-1"></i>
                                                {getWinnerName(competition)}
                                            </span>
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                    {hasEditPermission && (
                                        <td className="text-end text-nowrap">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                as={Link}
                                                to={`/competition/${competition.competitionId}/edit`}
                                                className="me-2"
                                            >
                                                <i className="bi bi-pencil me-1"></i>
                                                Edit
                                            </Button>

                                            <Dropdown className="d-inline-block">
                                                <Dropdown.Toggle
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    id={`force-state-${competition.competitionId}`}
                                                    disabled={forcingState === competition.competitionId}
                                                >
                                                    {forcingState === competition.competitionId ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                            Updating…
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-arrow-repeat me-1"></i>
                                                            Force State
                                                        </>
                                                    )}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="end">
                                                    {ADMIN_FORCE_STATE_OPTIONS.map(option => {
                                                        if (option === competition.state) return null;
                                                        let variant = 'secondary';
                                                        if (option === 'active') variant = 'success';
                                                        if (option === 'upcoming') variant = 'primary';
                                                        return (
                                                            <Dropdown.Item
                                                                key={option}
                                                                className={`text-${variant}`}
                                                                onClick={() => handleForceState(competition.competitionId, option)}
                                                            >
                                                                {getStatusLabel(option)}
                                                            </Dropdown.Item>
                                                        );
                                                    })}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <div className="admin-competitions-empty">
                    <div className="admin-competitions-empty-icon">🏆</div>
                    <h3>No competitions found</h3>
                    <p>
                        {activeTab === 'all'
                            ? 'There are no competitions yet.'
                            : `No ${TAB_LABELS[activeTab].toLowerCase()} competitions.`}
                    </p>
                </div>
            )}
        </Container>
    );
};

export default AdminCompetitions;
